import type { ReactNode } from 'react';
import { Card, Divider, Heading, Text, VStack } from '@astryxdesign/core';

const LAST_UPDATED = 'August 17, 2026';

interface Item {
  title: string;
  body: ReactNode;
}

const SECTIONS: Item[] = [
  {
    title: '1. Agreement',
    body: (
      <>
        These Terms of Service ("Terms") govern access to and use of VecTime Cloud's website,
        dashboard, and forecasting API (together, the "Service"), operated by VecTime Cloud
        ("VecTime", "we", "us"). By creating an account, calling the API, or otherwise using the
        Service, you agree to these Terms. If you are using the Service on behalf of an
        organization, you are agreeing on its behalf and confirming you have authority to do so.
      </>
    ),
  },
  {
    title: '2. The Service',
    body: (
      <>
        VecTime provides a metered HTTP API for time-series forecasting, plus a dashboard for
        managing API keys, plans, and usage. Plan tiers set your monthly call quota, requests-per-
        minute rate limit, and request size limits; the current limits for each plan are shown on
        our Pricing page and enforced by the API itself.
      </>
    ),
  },
  {
    title: '3. Accounts and API keys',
    body: (
      <>
        You are responsible for the accuracy of your account information and for all activity that
        occurs under your account and API keys. Keep your password and API keys confidential; treat
        an API key as a credential, not a shareable identifier. Notify us promptly at the contact
        below if you believe a key or your account has been compromised — you can also revoke and
        reissue keys yourself from the dashboard at any time.
      </>
    ),
  },
  {
    title: '4. Acceptable use',
    body: (
      <>
        <Text size="sm" color="secondary">You agree not to: circumvent or attempt to circumvent
        rate limits or quotas; share, sell, or sublicense API keys to third parties outside your own
        organization; use the Service to build a directly competing forecasting API; submit data you
        do not have the right to process; or use the Service in a way that is unlawful, infringing,
        or that disrupts the infrastructure other customers rely on.</Text>
        <Text size="sm" color="secondary">We may suspend or rate-limit access that we reasonably
        believe violates this section, and will make a reasonable effort to notify you when we do
        so.</Text>
      </>
    ),
  },
  {
    title: '5. Fees and billing',
    body: (
      <>
        Paid plans are billed in advance on a monthly (or otherwise stated) cycle through Stripe.
        Upgrades take effect immediately and are prorated for the current period; downgrades and
        cancellations take effect at the end of the current billing period, and you keep the higher
        tier's access until then. Fees are non-refundable except where required by law or stated
        otherwise at the time of purchase. If a payment fails, we may downgrade your account to the
        free tier after a grace period rather than interrupting the Service outright.
      </>
    ),
  },
  {
    title: '6. Your data',
    body: (
      <>
        You retain all rights to the data you submit to the API for forecasting. You grant us only
        the rights needed to process that data to return a response to you; we do not use it to
        train models or share it with third parties. See our{' '}
        <a href="/privacy">Privacy Policy</a> for details on what we retain and for how long.
      </>
    ),
  },
  {
    title: '7. Intellectual property',
    body: (
      <>
        The Service, including its software, documentation, and branding, is owned by VecTime or
        its licensors and is protected by intellectual property law. These Terms grant you a
        limited, non-exclusive, non-transferable right to use the Service; they do not transfer any
        ownership to you.
      </>
    ),
  },
  {
    title: '8. Disclaimers',
    body: (
      <>
        Forecasts returned by the Service are statistical estimates, not guarantees, and should not
        be the sole basis for decisions where inaccuracy could cause significant harm. The Service
        is provided "as is" and "as available," without warranties of any kind, express or implied,
        including fitness for a particular purpose, to the maximum extent permitted by law.
      </>
    ),
  },
  {
    title: '9. Limitation of liability',
    body: (
      <>
        To the maximum extent permitted by law, VecTime will not be liable for any indirect,
        incidental, special, consequential, or punitive damages, or for lost profits or data, arising
        from your use of the Service. Our total liability for any claim arising from these Terms or
        the Service is limited to the amount you paid us in the three months before the claim arose.
      </>
    ),
  },
  {
    title: '10. Termination',
    body: (
      <>
        You may stop using the Service and close your account at any time. We may suspend or
        terminate your access for material breach of these Terms, non-payment, or if required to
        comply with the law, and will make a reasonable effort to give notice unless doing so would
        create risk to the Service or its users. Sections that by their nature should survive
        termination — including billing, disclaimers, and limitation of liability — will survive.
      </>
    ),
  },
  {
    title: '11. Changes to the Service or these Terms',
    body: (
      <>
        We may update the Service and these Terms over time. If we make a material change to the
        Terms, we will update the date below and, for significant changes, notify account holders by
        email before the change takes effect. Continued use of the Service after a change takes
        effect constitutes acceptance of the updated Terms.
      </>
    ),
  },
  {
    title: '12. Contact',
    body: (
      <>
        Questions about these Terms: <span className="mono">legal@vectime.cloud</span>.
      </>
    ),
  },
];

export function TermsPage() {
  return (
    <div className="shell section-block">
      <VStack gap={4}>
        <VStack gap={1}>
          <Heading level={1}>Terms of Service</Heading>
          <Text color="secondary">Last updated {LAST_UPDATED}</Text>
        </VStack>

        <Card padding={3} variant="muted">
          <Text size="sm" color="secondary">
            <span className="measure--wide">
              This is a plain-language summary, not a substitute for legal advice. If you need terms
              tailored to a specific jurisdiction or use case, consult a lawyer.
            </span>
          </Text>
        </Card>

        <VStack gap={4}>
          {SECTIONS.map((s, i) => (
            <VStack gap={1} key={s.title}>
              <Heading level={2}>{s.title}</Heading>
              <div className="measure--wide">
                <VStack gap={1}>{s.body}</VStack>
              </div>
              {i < SECTIONS.length - 1 && <Divider variant="subtle" />}
            </VStack>
          ))}
        </VStack>
      </VStack>
    </div>
  );
}
